import React, { useState } from 'react'
import Button from 'components/button'
import Modal from 'components/modal'
import api from 'helpers/api'
import useProject from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { IEmbedSubmission } from 'types'

interface EmbedSubmissionBarProps {
  submission: IEmbedSubmission
  processId: string
  sectionId: string
  canReset: boolean
  canSubmit: boolean
  onChange: (submissions: IEmbedSubmission[]) => void
}

interface ConfirmActionModalProps {
  action: 'submit' | 'reset'
  title: string
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
  children: React.ReactNode
}

// Submitting and resetting can't be undone from here, so each asks first.
const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  action,
  title,
  busy,
  onCancel,
  onConfirm,
  children
}) => (
  <Modal
    id={`embed-${action}-modal`}
    open
    // Block dismissal while the request is in flight so it can't be interrupted.
    setOpen={() => {
      if (!busy) onCancel()
    }}
    showCross={!busy}
    title={title}
  >
    {children}
    <div className="flex items-center justify-end mt-5">
      <Button data-testid={`embed-${action}-cancel`} internalType="danger" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      <Button
        data-testid={`embed-${action}-confirm`}
        className="ml-2.5"
        internalType="accept"
        isLoading={busy}
        disabled={busy}
        onClick={onConfirm}
      >
        Confirm
      </Button>
    </div>
  </Modal>
)

const EmbedSubmissionBar: React.FC<EmbedSubmissionBarProps> = ({
  submission,
  processId,
  sectionId,
  canReset,
  canSubmit,
  onChange
}) => {
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  // Which action's confirmation is open. Kept apart so a reset that unlocks the table doesn't briefly
  // show the submit confirmation.
  const [confirming, setConfirming] = useState<'submit' | 'reset' | null>(null)
  const [busy, setBusy] = useState<boolean>(false)
  // Submitted means locked until reset, whatever the reset setting says now: an owner can turn reset on
  // (or template sync can) after the table was submitted, and the server only resets on the next submit.
  const locked = !!submission.submittedAt

  const run = (call: Promise<{ data: { submissions?: IEmbedSubmission[] } }>, message: string): void => {
    setBusy(true)
    call
      .then((apiResponse) => {
        onChange(apiResponse.data.submissions ?? [])
        setSnackbarMessage({ status: 'success', message })
      })
      .catch((error) => displayErrorMessage(error))
      .finally(() => {
        setBusy(false)
        setConfirming(null)
      })
  }

  if (locked) {
    return (
      <div className="flex shrink-0 items-center justify-between mt-2">
        <span className="font-semibold">Locked – Response Received</span>
        {canReset && (
          <Button data-testid="embed-reset" disabled={busy} onClick={() => setConfirming('reset')}>
            Reset Response
          </Button>
        )}
        {canReset && confirming === 'reset' && (
          <ConfirmActionModal
            action="reset"
            title="Reset Response?"
            busy={busy}
            onCancel={() => setConfirming(null)}
            onConfirm={() =>
              run(
                api.resetEmbedSubmission(project.publicId, processId, sectionId, submission.publicId),
                'Response reset.'
              )
            }
          >
            Do not reset this response if there is information you would like to keep in the response.
          </ConfirmActionModal>
        )}
      </div>
    )
  }

  if (!submission.enableSubmission || !canSubmit) return null

  return (
    <div className="flex shrink-0 justify-end mt-2">
      <Button data-testid="embed-submit" className="max-w-[170px]" onClick={() => setConfirming('submit')}>
        Submit Response
      </Button>
      {confirming === 'submit' && (
        <ConfirmActionModal
          action="submit"
          title="Submit Response"
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={() =>
            run(
              api.submitEmbedSubmission(project.publicId, processId, sectionId, submission.publicId),
              'Response submitted successfully.'
            )
          }
        >
          Are you sure you would like to submit this response? You will not be able to change this response once it has
          been submitted.
        </ConfirmActionModal>
      )}
    </div>
  )
}

export default EmbedSubmissionBar
